import React, { useState, useEffect, useCallback } from "react";
import InnerLayout from "../../../layouts/inner-layout";
import { ChevronLeftIcon } from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";
import { RefreshCw } from "lucide-react";
import DatePickerInput from "../../../forms/date-picker";
import { api } from "../../../../redux/apis/api";
import successToast from "../../../helper-components/success-toast";
import errorToast from "../../../helper-components/error-toast";
import {
    getBulkSyncState,
    formatDashboardCountdown,
    setSyncFrozenNow,
    DASHBOARD_SYNC_FREEZE_DURATION_MS,
} from "../../../helper-components/sync-freeze";

const getToday = () => new Date().toLocaleDateString("sv-SE");

const SYNC_MODULES = [
    { key: "billing", apiType: "billing", name: "Billing", endpoint: "/order/hard_sync" },
    { key: "return_bill", apiType: "return billing", name: "Return Bill", endpoint: "/return-order/hard-sync" },
    { key: "purchase_order", apiType: "purchase order", name: "Purchase Order", endpoint: "/purchaseorder/hard-sync-intercmy-and-normal" },
    { key: "purchase_invoice", apiType: "purchase invoice", name: "Purchase Invoice", endpoint: "/purchaseinvoice/hard-sync-normal" },
    { key: "purchase_return", apiType: "purchase return", name: "Purchase Return", endpoint: "/purchasereturn/hard-sync-intercmy-and-normal" },
];

const SyncDashboard = () => {
    const navigate = useNavigate();
    const today = getToday();

    const [rows, setRows] = useState(
        SYNC_MODULES.map((mod) => ({
            ...mod,
            fromDate: "",
            toDate: "",
            syncing: false,
            lastSynced: null,
        }))
    );

    const updateRow = (key, patch) => {
        setRows((prev) => prev.map((row) => (row.key === key ? { ...row, ...patch } : row)));
    };

    // Pulls last_synced per module type from the backend; this (with a 1 hour cooldown)
    // is the source of truth for whether each module's Sync button is enabled.
    const fetchTimings = useCallback(async () => {
        try {
            const res = await api.get("/sync-dashboard/timings");
            const timings = res?.data?.data ?? [];
            setRows((prev) =>
                prev.map((row) => {
                    const match = timings.find((t) => t?.type === row.apiType);
                    return match ? { ...row, lastSynced: match.last_synced ?? null } : row;
                })
            );
        } catch (error) {
            // ignore; buttons fall back to enabled if timings are unavailable
        }
    }, []);

    useEffect(() => {
        fetchTimings();
    }, [fetchTimings]);

    // Tick every second so the countdown updates and the button re-enables automatically
    // once the 1 hour cooldown passes, without needing a manual refresh.
    useEffect(() => {
        const interval = setInterval(() => setRows((prev) => [...prev]), 1000);
        return () => clearInterval(interval);
    }, []);

    const handleSync = async (key) => {
        const row = rows.find((r) => r.key === key);
        if (!row.fromDate || !row.toDate) {
            errorToast("Please select both From and To dates");
            return;
        }
        const syncState = getBulkSyncState({
            moduleKey: `sync_dashboard_${key}`,
            lastSynced: row.lastSynced,
        });
        if (row.syncing || syncState.disabled) return;

        updateRow(key, { syncing: true });

        try {
            await api.post(row.endpoint, {}, {
                params: { from_date: row.fromDate, to_date: row.toDate },
            });
            // Freeze locally right away so the button can't be re-triggered while the
            // refreshed server timing (fetchTimings) is still in flight or fails.
            setSyncFrozenNow(`sync_dashboard_${key}`, DASHBOARD_SYNC_FREEZE_DURATION_MS);
            successToast(`${row.name} synced successfully`);
            fetchTimings();
        } catch (error) {
            errorToast(error.response?.data?.message || `Failed to sync ${row.name}`);
        } finally {
            updateRow(key, { syncing: false });
        }
    };

    const tableHeading = ["Module", "From Date", "To Date", "Last Synced", "Action"];

    return (
        <InnerLayout list={true} title="Sync Dashboard" module="sync-dashboard">
            <div className="flex flex-col h-full bg-white relative">
                {/* Header Breadcrumb */}
                <div className="flex justify-between items-center pt-5 mx-6 mb-6">
                    <div className="flex items-center gap-2 text-sml">
                        <button
                            className="flex items-center gap-2"
                            onClick={() => {
                                navigate("/settings");
                            }}
                        >
                            <ChevronLeftIcon className="h-5 w-5 inline-block" />
                            System Settings /
                        </button>
                        <span className="cursor-pointer">{" " + "Sync Dashboard"}</span>
                    </div>
                </div>

                {/* Table Container */}
                <div className="mx-6 flex-1 overflow-hidden pb-4 flex flex-col">
                    <div className="overflow-auto rounded-xl border border-[var(--list-table-border-color)] bg-white flex-1 custom-scrollbar shadow-sm">
                        <table className="w-full text-left border-collapse min-w-max">
                            <thead className="sticky top-0 bg-[var(--list-table-heading-bg)] z-[10]">
                                <tr className="relative h-[50px]">
                                    {tableHeading.map((heading) => (
                                        <th
                                            key={heading}
                                            className="px-5 py-3 text-[var(--list-table-heading-fg-color)] text-[length:var(--list-table-heading-font-size)] font-[var(--list-table-heading-font-weight)] uppercase tracking-wider whitespace-nowrap border-b border-[var(--list-table-border-color)]"
                                        >
                                            {heading}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-[var(--list-table-border-color)]">
                                {rows.map((row) => {
                                    const syncState = getBulkSyncState({
                                        moduleKey: `sync_dashboard_${row.key}`,
                                        lastSynced: row.lastSynced,
                                    });
                                    return (
                                        <tr
                                            key={row.key}
                                            className="transition-all duration-200 h-[56px] group bg-[var(--list-table-row-bg)] hover:bg-[var(--list-table-row-hover-bg)]"
                                        >
                                            <td className="px-5 py-3 whitespace-nowrap min-w-[180px] border-b border-[var(--list-table-border-color)] font-medium text-sm text-gray-800">
                                                {row.name}
                                            </td>
                                            <td className="px-5 py-3 whitespace-nowrap border-b border-[var(--list-table-border-color)]">
                                                <DatePickerInput
                                                    value={row.fromDate}
                                                    onChange={(val) => updateRow(row.key, { fromDate: val })}
                                                    placeholder="From Date"
                                                    maxDate={row.toDate && row.toDate < today ? row.toDate : today}
                                                />
                                            </td>
                                            <td className="px-5 py-3 whitespace-nowrap border-b border-[var(--list-table-border-color)]">
                                                <DatePickerInput
                                                    value={row.toDate}
                                                    onChange={(val) => updateRow(row.key, { toDate: val })}
                                                    placeholder="To Date"
                                                    minDate={row.fromDate}
                                                    maxDate={today}
                                                />
                                            </td>
                                            <td className="px-5 py-3 whitespace-nowrap border-b border-[var(--list-table-border-color)] text-sm text-gray-500">
                                                {syncState.lastSyncedLabel}
                                            </td>
                                            <td className="px-5 py-3 whitespace-nowrap border-b border-[var(--list-table-border-color)]">
                                                <button
                                                    className="bg-[var(--primary-button-bg-color)] text-white px-4 py-1.5 rounded-md text-[11px] font-semibold flex items-center gap-1.5 hover:bg-blue-600 shadow-sm transition-all active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed min-w-[92px] justify-center"
                                                    onClick={() => handleSync(row.key)}
                                                    disabled={row.syncing || syncState.disabled}
                                                    title={syncState.disabled ? "This module was recently synced. Please wait before syncing again." : ""}
                                                >
                                                    <RefreshCw
                                                        size={12}
                                                        strokeWidth={2.5}
                                                        className={row.syncing ? "animate-spin" : ""}
                                                    />
                                                    {row.syncing ? "Syncing..." : syncState.disabled ? formatDashboardCountdown(syncState.remainingMs) : "Sync"}
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </InnerLayout>
    );
};

export default SyncDashboard;
